import React, { useMemo } from 'react';
import { formatNormalizedScorePercent } from 'js/utils/format';
import { median } from 'js/utils/math';
import {
  makeRollupWeightFn,
  rollupWeightedMedian,
  rollupWeightedScores,
} from 'js/utils/question-set';
import { mean } from 'lodash';
import classNames from 'classnames';
import SetDetailChart from '../SetDetailChart';
import Variability from '../Variability';
import {
  listQuestionSets,
  radialPieOverviewDetailData,
  radialPieWedges,
} from '../CircleChart/radialPieWedges';

const AssessmentSubview = ({ questionSets, categoryColorMap }) => {
  const { flattenToQuestions, wedges } = useMemo(
    () => radialPieWedges(questionSets),
    [questionSets]
  );

  const detailData = useMemo(
    () => radialPieOverviewDetailData(questionSets, categoryColorMap),
    [questionSets, categoryColorMap]
  );

  const questionSetList = listQuestionSets(questionSets);
  const rollupItems = flattenToQuestions
    ? wedges.map((wedge) => wedge.question)
    : questionSetList;
  const getRollupWeight = makeRollupWeightFn(rollupItems);
  const usesRubricWeights = rollupItems.some(
    (item) => item?.weight != null && item.weight !== ''
  );

  const leafScores = flattenToQuestions
    ? wedges
        .map((wedge) => wedge.normalizedScore)
        .filter((score) => score != null)
    : detailData.map((d) => d.normalizedScore).filter((score) => score != null);

  const medianScore = usesRubricWeights
    ? rollupWeightedMedian(rollupItems, {
        getScore: (item) => item.normalizedScore,
        getWeight: getRollupWeight,
      })
    : leafScores.length === 0
    ? null
    : median(leafScores);
  const meanScore = usesRubricWeights
    ? rollupWeightedScores(rollupItems, {
        getScore: (item) => item.normalizedScore,
        getWeight: getRollupWeight,
      })
    : leafScores.length === 0
    ? null
    : mean(leafScores);
  const { score: varianceRawScore, count: varianceCount } = detailData.reduce(
    (acc, d) => {
      const stdDev =
        d.normalizedVariance === null ? null : Math.sqrt(d.normalizedVariance);

      const normalizedDev =
        d.normalizedScore === null
          ? null
          : Math.abs(meanScore - d.normalizedScore);

      let selfScore = 0;
      if (normalizedDev >= 0.2) {
        selfScore = 2;
      } else if (normalizedDev >= 0.07) {
        selfScore = 1;
      }

      let childrenScore = 0;
      if (stdDev >= 0.2) {
        childrenScore = 2;
      } else if (stdDev >= 0.07) {
        childrenScore = 1;
      }

      const score = selfScore + childrenScore;

      return normalizedDev !== null || stdDev !== null
        ? { score: acc.score + score, count: acc.count + 1 }
        : acc;
    },
    { score: 0, count: 0 }
  );
  const varianceScore =
    varianceCount === 0 ? null : varianceRawScore / varianceCount;

  return (
    <>
      <section className="figure-stats">
        <label>Viewing</label>
        <h3 className="view-name">
          {flattenToQuestions ? 'Questions' : 'Assessment overview'}
        </h3>
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td>
                <label>Mean Score</label>
                <h5
                  className={classNames({ 'text-muted': meanScore === null })}
                >
                  {meanScore === null
                    ? 'N/A'
                    : formatNormalizedScorePercent(meanScore)}
                </h5>
              </td>
              <td>
                <label>Median Score</label>
                <h5
                  className={classNames({ 'text-muted': medianScore === null })}
                >
                  {medianScore === null
                    ? 'N/A'
                    : formatNormalizedScorePercent(medianScore)}
                </h5>
              </td>
            </tr>
            <tr>
              <td>
                <label>Variability</label>
                <Variability varianceScore={varianceScore} />
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </section>
      <section className="figure-spacer"></section>
      <section className="figure-detail">
        <label>Aggregate Scores</label>
        <SetDetailChart data={detailData} />
      </section>
    </>
  );
};

export default AssessmentSubview;
